import { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, ShieldCheck, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { reputation } from '../lib/api';

/**
 * Merchant-only widget. Two responsibilities:
 *   1) Show a High-Risk badge if this customer's private avg < 3 stars.
 *   2) Let the merchant rate this customer (after at least one settled Khata bill).
 *
 * Backend enforces: only merchants + admins can read customer ratings, and
 * only merchants who have a `repay` ledger entry for this (store, customer)
 * can post one.
 */
export default function KhataCustomerRating({ storeId, customerPhone }) {
  const [data, setData] = useState({ count: 0, average: null, high_risk: false, reviews: [] });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');

  const load = useCallback(() => {
    if (!customerPhone) return;
    reputation.customerRatings(customerPhone)
      .then(setData)
      .catch(() => setData({ count: 0, average: null, high_risk: false, reviews: [] }));
  }, [customerPhone]);
  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await reputation.rateCustomer({
        customer_phone: customerPhone,
        store_id: storeId,
        rating,
        text: text.trim() || null,
      });
      toast.success('Customer rating saved (private)');
      setOpen(false); setText(''); setRating(5);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save rating');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid={`khata-rating-${customerPhone}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {data.count === 0
          ? <span className="text-[10px] uppercase tracking-wider bg-[#F5F5F5] text-ink/60 px-1.5 py-0.5 rounded-full">
              Not yet rated
            </span>
          : data.high_risk
            ? (
              <span data-testid={`khata-high-risk-${customerPhone}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#FFEBEE] text-[#C62828] px-2 py-0.5 rounded-full">
                <AlertTriangle className="w-3 h-3"/>High risk · {data.average}★
              </span>
            )
            : (
              <span data-testid={`khata-good-risk-${customerPhone}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E8F5E9] text-[#1B5E20] px-2 py-0.5 rounded-full">
                <ShieldCheck className="w-3 h-3"/>Reliable · {data.average}★ · {data.count} rating{data.count !== 1 ? 's' : ''}
              </span>
            )
        }
        <button data-testid={`khata-rate-open-${customerPhone}`} onClick={() => setOpen(true)}
          className="text-[10px] font-semibold uppercase tracking-wider text-[#D35400] hover:underline">
          Rate reliability
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-6"
            onClick={e => e.stopPropagation()}
            data-testid={`khata-rate-dialog-${customerPhone}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#D35400]">Private rating</div>
                <h3 className="font-heading font-black text-xl mt-1">Rate {customerPhone}</h3>
                <div className="text-xs text-ink/60 mt-1">
                  Only merchants and admins can see this — never the customer.
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-ink/40 hover:text-ink/80"><X className="w-4 h-4"/></button>
            </div>
            <div className="mt-3 flex items-center gap-1">
              {[1,2,3,4,5].map(n => (
                <button key={n} data-testid={`khata-rate-star-${n}`} onClick={() => setRating(n)} className="p-1">
                  <Star className={`w-7 h-7 ${rating >= n ? 'fill-[#F1C40F] text-[#F1C40F]' : 'text-ink/30'}`}/>
                </button>
              ))}
            </div>
            <textarea data-testid="khata-rate-note" value={text} onChange={e => setText(e.target.value)}
              rows={3} placeholder="What was your experience? (Payment speed, honesty, disputes…)"
              className="w-full mt-3 border border-border bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20"/>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="pill !bg-white">Cancel</button>
              <button data-testid="khata-rate-submit" disabled={busy} onClick={submit} className="btn-primary">
                {busy ? 'Saving…' : 'Save private rating'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
